import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type ChangeEvent, type CSSProperties, type InputHTMLAttributes, type KeyboardEvent, type MouseEvent, type RefObject } from "react";
import { cn } from "../cn";

// The Clevis date and time fields. Drop-ins for <input type="date"> and
// <input type="time">: same value strings ("2026-10-28", "14:30"), same
// onChange (e.target.value). Instead of the browser's widgets they show a
// readable value ("Tue, Oct 28", "2:30 PM"), accept typing in any sensible
// form ("10/28", "oct 28", "tomorrow"; "230p", "2:30 pm", "1430"), and open a
// branded panel: a month calendar with Today / Tomorrow, or a list of times.
// On a touch screen the field is tap-only (no keyboard pops up); the same
// panel opens and a tap picks.

type Props = Omit<InputHTMLAttributes<HTMLInputElement>, "type" | "value" | "onChange"> & {
  value?: string;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
};

const field =
  "flex items-center gap-2 rounded-lg border border-steel/40 bg-white px-3 py-2 text-sm text-ink focus-within:border-brandorange focus-within:ring-1 focus-within:ring-brandorange";
const fullUnlessSized = (className?: string) => !/(^|\s)(w-|flex-1)/.test(className ?? "") && "w-full";
const panelClass = "fixed z-[1000] overflow-hidden rounded-xl border border-steel/25 bg-white text-sm text-ink shadow-xl shadow-ink/10";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const MONTH_ABBR = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
const pad = (n: number) => String(n).padStart(2, "0");

// ---- dates -----------------------------------------------------------------

export const toIsoDate = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
export function fromIsoDate(s: string | undefined | null): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s ?? "");
  if (!m) return null;
  return valid(Number(m[1]), Number(m[2]), Number(m[3]));
}
function valid(y: number, mo: number, d: number): Date | null {
  const dt = new Date(y, mo - 1, d);
  return dt.getFullYear() === y && dt.getMonth() === mo - 1 && dt.getDate() === d ? dt : null;
}

/** "Tue, Oct 28" this year, "Oct 28, 2027" otherwise. */
export function formatDate(iso: string | undefined | null, today = new Date()): string {
  const d = fromIsoDate(iso);
  if (!d) return "";
  return d.getFullYear() === today.getFullYear()
    ? d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })
    : d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

/** What a person typed → ISO date, or null. "10/28", "10/28/26", "oct 28", "28 oct 2026", "2026-10-28", "today", "tomorrow", "+3" (days). */
export function parseTypedDate(text: string, today = new Date()): string | null {
  const t = text.trim().toLowerCase().replace(/,/g, " ").replace(/\s+/g, " ");
  if (!t) return null;
  const shift = (n: number) => toIsoDate(new Date(today.getFullYear(), today.getMonth(), today.getDate() + n));
  if (t === "today" || t === "t") return shift(0);
  if (t === "tomorrow" || t === "tom") return shift(1);
  if (t === "yesterday") return shift(-1);
  let m = /^\+(\d{1,3})$/.exec(t);
  if (m) return shift(Number(m[1]));
  m = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(t);
  if (m) {
    const d = valid(Number(m[1]), Number(m[2]), Number(m[3]));
    return d ? toIsoDate(d) : null;
  }
  m = /^(\d{1,2})[/.-](\d{1,2})(?:[/.-](\d{2}|\d{4}))?$/.exec(t);
  if (m) {
    const y = m[3] ? (m[3].length === 2 ? 2000 + Number(m[3]) : Number(m[3])) : today.getFullYear();
    const d = valid(y, Number(m[1]), Number(m[2]));
    return d ? toIsoDate(d) : null;
  }
  // "oct 28", "oct 28 2026", "28 oct"
  m = /^([a-z]{3,})\.? (\d{1,2})(?: (\d{4}))?$/.exec(t);
  let monthWord = m?.[1];
  let dayText = m?.[2];
  if (!m) {
    m = /^(\d{1,2}) ([a-z]{3,})\.?(?: (\d{4}))?$/.exec(t);
    monthWord = m?.[2];
    dayText = m?.[1];
  }
  if (m && monthWord && dayText) {
    const mo = MONTH_ABBR.indexOf(monthWord.slice(0, 3)) + 1;
    if (!mo) return null;
    const d = valid(m[3] ? Number(m[3]) : today.getFullYear(), mo, Number(dayText));
    return d ? toIsoDate(d) : null;
  }
  return null;
}

function usePanel(open: boolean, anchor: RefObject<HTMLElement | null>, panel: RefObject<HTMLElement | null>, onOutside: () => void, size: { w: number; h: number }) {
  const [pos, setPos] = useState<CSSProperties>({});
  const outside = useRef(onOutside);
  outside.current = onOutside;
  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const b = anchor.current?.getBoundingClientRect();
      if (!b) return;
      const below = window.innerHeight - b.bottom;
      const up = below < size.h + 12 && b.top > below;
      const left = Math.max(8, Math.min(b.left, window.innerWidth - size.w - 8));
      setPos(up ? { left, bottom: window.innerHeight - b.top + 4, width: size.w } : { left, top: b.bottom + 4, width: size.w });
    };
    place();
    const onScroll = (e: Event) => {
      if (panel.current && e.target instanceof Node && panel.current.contains(e.target)) return;
      place();
    };
    const onDown = (e: Event) => {
      const t = e.target as Node;
      if (panel.current?.contains(t) || anchor.current?.contains(t)) return;
      outside.current();
    };
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", place);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", place);
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
    };
  }, [open, anchor, panel, size.w, size.h]);
  return pos;
}

/** A touch screen (phone or tablet): no fine pointer, so the field is tap-only and the keyboard stays down. */
function useTouch(): boolean {
  const [touch, setTouch] = useState(() => typeof window !== "undefined" && window.matchMedia("(pointer: coarse)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(pointer: coarse)");
    const on = () => setTouch(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return touch;
}

const fire = (onChange: Props["onChange"], value: string, name?: string) => {
  const target = { value, name: name ?? "" };
  onChange?.({ target, currentTarget: target } as unknown as ChangeEvent<HTMLInputElement>);
};

const DATE_SIZE = { w: 272, h: 340 };

export function DateInput({ value, onChange, className, disabled, required, min, max, name, id, placeholder, title, autoFocus, "aria-label": ariaLabel }: Props) {
  const current = typeof value === "string" ? value : "";
  const today = useMemo(() => toIsoDate(new Date()), []);
  const tomorrow = useMemo(() => parseTypedDate("tomorrow")!, []);
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(""); // what's being typed while the field has focus
  const [typing, setTyping] = useState(false);
  const [dirty, setDirty] = useState(false); // typed something since the field opened
  const [view, setView] = useState(() => (fromIsoDate(current) ?? new Date()).getTime());
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const listId = useId();
  const touch = useTouch();

  const typed = typing && dirty ? parseTypedDate(text) : null;
  const shown = typed ?? current; // the day the calendar highlights
  const viewDate = new Date(view);
  const minIso = typeof min === "string" ? min : "";
  const maxIso = typeof max === "string" ? max : "";
  const allowed = (iso: string) => (!minIso || iso >= minIso) && (!maxIso || iso <= maxIso);

  function commit(iso: string) {
    if (iso && !allowed(iso)) return;
    if (iso !== current) fire(onChange, iso, name);
    setTyping(false);
    setOpen(false);
  }
  function finishTyping() {
    // Enter, Tab or a click elsewhere: keep what was typed if it's a date, else go back.
    if (typing && dirty) {
      if (text.trim() === "") commit("");
      else if (typed) commit(typed);
    }
    setTyping(false);
    setOpen(false);
  }
  const pos = usePanel(open, wrapRef, panelRef, finishTyping, DATE_SIZE);

  // Typing a date moves the calendar to that month.
  useEffect(() => {
    const d = fromIsoDate(shown);
    if (d && (d.getMonth() !== viewDate.getMonth() || d.getFullYear() !== viewDate.getFullYear())) setView(d.getTime());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shown]);

  function start() {
    if (disabled || open) return;
    setView((fromIsoDate(current) ?? new Date()).getTime());
    setText(formatDate(current));
    setDirty(false);
    setTyping(true);
    setOpen(true);
    if (!touch) requestAnimationFrame(() => inputRef.current?.select());
  }
  function onKey(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter") {
      e.preventDefault();
      finishTyping();
    } else if (e.key === "Escape") {
      e.preventDefault();
      setTyping(false);
      setOpen(false);
    } else if (e.key === "Tab") {
      finishTyping();
    } else if (e.key === "ArrowDown" && !open) {
      e.preventDefault();
      setOpen(true);
    }
  }

  // Calendar cells: Sunday-first weeks, six rows, so the panel never changes height.
  const first = new Date(viewDate.getFullYear(), viewDate.getMonth(), 1);
  const cells: Date[] = [];
  for (let i = 0; i < 42; i++) cells.push(new Date(first.getFullYear(), first.getMonth(), 1 - first.getDay() + i));
  const moveMonth = (n: number) => setView(new Date(viewDate.getFullYear(), viewDate.getMonth() + n, 1).getTime());
  const keepFocus = (e: MouseEvent) => e.preventDefault();

  return (
    <div ref={wrapRef} className={cn(field, fullUnlessSized(className), disabled && "cursor-not-allowed bg-cream text-steel", className)} title={title}>
      <input
        ref={inputRef}
        id={id}
        type="text"
        role="combobox"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={listId}
        aria-label={ariaLabel}
        className="min-w-0 flex-1 bg-transparent outline-none placeholder:text-steel disabled:cursor-not-allowed"
        value={typing ? text : formatDate(current)}
        placeholder={placeholder ?? "Pick a date"}
        disabled={disabled}
        required={required}
        autoFocus={autoFocus}
        autoComplete="off"
        readOnly={touch}
        inputMode={touch ? "none" : undefined}
        onFocus={start}
        onClick={start}
        onChange={(e) => {
          setText(e.target.value);
          setDirty(true);
          setTyping(true);
          setOpen(true);
        }}
        onKeyDown={onKey}
      />
      {name ? <input type="hidden" name={name} value={current} /> : null}
      <svg viewBox="0 0 20 20" className="h-4 w-4 shrink-0 text-steel" aria-hidden="true">
        <rect x="3" y="4.5" width="14" height="12.5" rx="2" fill="none" stroke="currentColor" strokeWidth="1.5" />
        <path d="M3 8.5h14M7 2.5v3M13 2.5v3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
      {open ? (
        <div ref={panelRef} id={listId} role="dialog" className={cn(panelClass, "p-2")} style={pos} onMouseDown={keepFocus}>
          <div className="flex items-center justify-between px-1 pb-1">
            <button type="button" className="rounded-md p-1 text-steel hover:bg-cream hover:text-ink" aria-label="Previous month" onClick={() => moveMonth(-1)}>
              <svg viewBox="0 0 20 20" className="h-4 w-4" aria-hidden="true">
                <path d="M12.5 5.5 8 10l4.5 4.5" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <span className="font-semibold">
              {MONTHS[viewDate.getMonth()]} {viewDate.getFullYear()}
            </span>
            <button type="button" className="rounded-md p-1 text-steel hover:bg-cream hover:text-ink" aria-label="Next month" onClick={() => moveMonth(1)}>
              <svg viewBox="0 0 20 20" className="h-4 w-4" aria-hidden="true">
                <path d="m7.5 5.5 4.5 4.5-4.5 4.5" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>
          <div className="grid grid-cols-7 text-center text-[11px] font-semibold uppercase tracking-wide text-steel">
            {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
              <span key={i} className="py-1">
                {d}
              </span>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-y-0.5 text-center">
            {cells.map((d) => {
              const iso = toIsoDate(d);
              const inMonth = d.getMonth() === viewDate.getMonth();
              const ok = allowed(iso);
              const sel = iso === shown;
              return (
                <button
                  key={iso}
                  type="button"
                  disabled={!ok}
                  aria-label={d.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}
                  aria-pressed={sel}
                  className={cn(
                    "mx-auto h-8 w-8 rounded-full text-sm",
                    sel ? "bg-brandorange font-semibold text-white" : ok ? "hover:bg-cream" : "cursor-not-allowed text-steel/40",
                    !sel && !inMonth && ok && "text-steel/60",
                    !sel && iso === today && "font-semibold text-brandorange ring-1 ring-inset ring-brandorange/50",
                  )}
                  onClick={() => commit(iso)}
                >
                  {d.getDate()}
                </button>
              );
            })}
          </div>
          <div className="mt-2 flex items-center gap-1 border-t border-steel/15 px-1 pt-2 text-xs">
            <button type="button" className="rounded-md px-2 py-1 font-medium text-ink hover:bg-cream disabled:text-steel/50" disabled={!allowed(today)} onClick={() => commit(today)}>
              Today
            </button>
            <button type="button" className="rounded-md px-2 py-1 font-medium text-ink hover:bg-cream disabled:text-steel/50" disabled={!allowed(tomorrow)} onClick={() => commit(tomorrow)}>
              Tomorrow
            </button>
            <span className="flex-1" />
            {current && !required ? (
              <button type="button" className="rounded-md px-2 py-1 text-steel hover:bg-cream hover:text-ink" onClick={() => commit("")}>
                Clear
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

// ---- times -----------------------------------------------------------------

/** "14:30" → "2:30 PM". */
export function formatTime(hm: string | undefined | null): string {
  const m = /^(\d{2}):(\d{2})/.exec(hm ?? "");
  if (!m) return "";
  const h = Number(m[1]);
  return `${h % 12 === 0 ? 12 : h % 12}:${m[2]} ${h < 12 ? "AM" : "PM"}`;
}

/** What a person typed → "HH:mm", or null. "2", "2p", "230", "2:30", "2:30 pm", "1430", "noon". Without am/pm: 7–11 is morning, 1–6 and 12 afternoon. */
export function parseTypedTime(text: string): string | null {
  const t = text.trim().toLowerCase().replace(/\s+/g, "").replace(/\./g, "");
  if (!t) return null;
  if (t === "noon" || t === "12n") return "12:00";
  if (t === "midnight") return "00:00";
  const m = /^(\d{1,4})(?::(\d{1,2}))?(a|p|am|pm)?$/.exec(t);
  if (!m) return null;
  let h: number;
  let min: number;
  if (m[2] !== undefined) {
    if (m[1]!.length > 2) return null;
    h = Number(m[1]);
    min = Number(m[2]);
  } else {
    const d = m[1]!;
    if (d.length <= 2) {
      h = Number(d);
      min = 0;
    } else {
      h = Number(d.slice(0, d.length - 2));
      min = Number(d.slice(-2));
    }
  }
  if (min > 59 || h > 24) return null;
  const ap = m[3]?.[0];
  if (ap) {
    if (h === 0 || h > 12) return null;
    if (ap === "a") h = h === 12 ? 0 : h;
    else h = h === 12 ? 12 : h + 12;
  } else if (h >= 1 && h <= 12) {
    // A bare hour: 7–11 means morning, 1–6 and 12 afternoon.
    if (h <= 6) h += 12;
  } else if (h === 24) h = 0;
  return `${pad(h)}:${pad(min)}`;
}

const TIME_SIZE = { w: 180, h: 260 };
const norm = (s: string) => s.toLowerCase().replace(/[\s:]/g, "");

export function TimeInput({ value, onChange, className, disabled, required, min, max, step, name, id, placeholder, title, autoFocus, "aria-label": ariaLabel }: Props) {
  const current = typeof value === "string" ? value.slice(0, 5) : "";
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [typing, setTyping] = useState(false);
  const [dirty, setDirty] = useState(false); // typed something since the field opened
  const [active, setActive] = useState(-1);
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const listId = useId();
  const touch = useTouch();
  const stepMin = Math.max(5, Math.round((typeof step === "number" ? step : Number(step) || 900) / 60));
  const minHm = typeof min === "string" ? min.slice(0, 5) : "";
  const maxHm = typeof max === "string" ? max.slice(0, 5) : "";
  const allowed = (hm: string) => (!minHm || hm >= minHm) && (!maxHm || hm <= maxHm);

  const all = useMemo(() => {
    const out: string[] = [];
    for (let m = 0; m < 24 * 60; m += stepMin) out.push(`${pad(Math.floor(m / 60))}:${pad(m % 60)}`);
    return out;
  }, [stepMin]);
  const typed = typing && dirty ? parseTypedTime(text) : null;
  const q = typing && dirty ? norm(text) : "";
  const shown = useMemo(() => {
    const list = all.filter(allowed);
    if (!q) return list;
    const hits = list.filter((hm) => norm(formatTime(hm)).startsWith(q) || hm.replace(":", "").startsWith(q));
    return hits.length ? hits : list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [all, q, minHm, maxHm]);
  const anchorTime = typed ?? current ?? "";

  function commit(hm: string) {
    if (hm && !allowed(hm)) return;
    if (hm !== current) fire(onChange, hm, name);
    setTyping(false);
    setOpen(false);
  }
  function finishTyping() {
    if (typing && dirty) {
      if (text.trim() === "") commit("");
      else if (typed) commit(typed);
      else if (active >= 0 && shown[active]) commit(shown[active]!);
    }
    setTyping(false);
    setOpen(false);
  }
  const pos = usePanel(open, wrapRef, panelRef, finishTyping, TIME_SIZE);

  function start() {
    if (disabled || open) return;
    setText(formatTime(current));
    setDirty(false);
    setTyping(true);
    setOpen(true);
    setActive(-1);
    if (!touch) requestAnimationFrame(() => inputRef.current?.select());
  }
  // Scroll the list to the current pick (or a working-day start) when it opens, and follow the highlight.
  useEffect(() => {
    if (!open) return;
    const i = anchorTime ? shown.findIndex((hm) => hm >= anchorTime) : shown.findIndex((hm) => hm >= "08:00");
    const el = listRef.current?.querySelector<HTMLElement>(`[data-i="${Math.max(0, i)}"]`);
    el?.scrollIntoView({ block: q ? "nearest" : "center" });
  }, [open, shown, anchorTime, q]);
  useEffect(() => {
    if (!open || active < 0) return;
    listRef.current?.querySelector<HTMLElement>(`[data-i="${active}"]`)?.scrollIntoView({ block: "nearest" });
  }, [open, active]);

  function onKey(e: KeyboardEvent<HTMLInputElement>) {
    const from = () => {
      const i = shown.findIndex((hm) => hm >= (anchorTime || "08:00"));
      return i < 0 ? shown.length - 1 : i;
    };
    if (e.key === "Enter") {
      e.preventDefault();
      if (active >= 0 && shown[active] && !typed) commit(shown[active]!);
      else finishTyping();
    } else if (e.key === "Escape") {
      e.preventDefault();
      setTyping(false);
      setOpen(false);
    } else if (e.key === "Tab") {
      finishTyping();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!open) setOpen(true);
      setActive((a) => Math.min(shown.length - 1, (a < 0 ? from() - 1 : a) + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(0, (a < 0 ? from() : a) - 1));
    }
  }

  return (
    <div ref={wrapRef} className={cn(field, fullUnlessSized(className), disabled && "cursor-not-allowed bg-cream text-steel", className)} title={title}>
      <input
        ref={inputRef}
        id={id}
        type="text"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-activedescendant={open && active >= 0 ? `${listId}-${active}` : undefined}
        aria-label={ariaLabel}
        className="min-w-0 flex-1 bg-transparent outline-none placeholder:text-steel disabled:cursor-not-allowed"
        value={typing ? text : formatTime(current)}
        placeholder={placeholder ?? "Time"}
        disabled={disabled}
        required={required}
        autoFocus={autoFocus}
        autoComplete="off"
        readOnly={touch}
        inputMode={touch ? "none" : undefined}
        onFocus={start}
        onClick={start}
        onChange={(e) => {
          setText(e.target.value);
          setDirty(true);
          setTyping(true);
          setActive(-1);
          setOpen(true);
        }}
        onKeyDown={onKey}
      />
      {name ? <input type="hidden" name={name} value={current} /> : null}
      <svg viewBox="0 0 20 20" className="h-4 w-4 shrink-0 text-steel" aria-hidden="true">
        <circle cx="10" cy="10" r="7" fill="none" stroke="currentColor" strokeWidth="1.5" />
        <path d="M10 6v4.3l2.8 1.7" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      {open ? (
        <div ref={panelRef} className={cn(panelClass, "flex flex-col")} style={{ ...pos, maxHeight: TIME_SIZE.h }} onMouseDown={(e) => e.preventDefault()}>
          <div ref={listRef} id={listId} role="listbox" className="min-h-0 flex-1 overflow-y-auto py-1">
            {shown.map((hm, i) => {
              const sel = hm === anchorTime;
              return (
                <div
                  key={hm}
                  id={`${listId}-${i}`}
                  data-i={i}
                  role="option"
                  aria-selected={sel}
                  className={cn("mx-1 cursor-pointer rounded-md px-2.5 py-1.5 tabular-nums", i === active && "bg-cream", sel && "bg-brandorange font-semibold text-white")}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => commit(hm)}
                >
                  {formatTime(hm)}
                </div>
              );
            })}
          </div>
          {current && !required ? (
            <button type="button" className="border-t border-steel/15 px-3 py-1.5 text-left text-xs text-steel hover:bg-cream hover:text-ink" onClick={() => commit("")}>
              Clear
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
